import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminChildren from "@/pages/admin/Children";
import AdminVehicles from "@/pages/admin/Vehicles";
import AdminRoutes from "@/pages/admin/Routes";
import AdminMissions from "@/pages/admin/Missions";
import AdminIncidents from "@/pages/admin/Incidents";
import AdminNotifications from "@/pages/admin/Notifications";
import AdminLiveTracking from "@/pages/admin/LiveTracking";
import AdminPricing from "@/pages/admin/Pricing";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminGtfsTrips from "@/pages/admin/GtfsTrips";
import AdminGtfsTripDetail from "@/pages/admin/GtfsTripDetail";
import AdminFleetMapping from "@/pages/admin/FleetMapping";
import AdminStopsMap from "@/pages/admin/StopsMap";
import AdminStops from "@/pages/admin/Stops";
import AdminPoiMbc from "@/pages/admin/PoiMbc";
import AdminInterStops from "@/pages/admin/InterStops";
import AdminBluetoothTags from "@/pages/admin/BluetoothTags";
import AdminAffectations from "@/pages/admin/Affectations";
import AdminAffectationsBulk from "@/pages/admin/AffectationsBulk";
import AdminPrograms from "@/pages/admin/Programs";
import AdminFleetAssignments from "@/pages/admin/FleetAssignments";
import AdminSubstitutions from "@/pages/admin/Substitutions";
import AdminExploitation from "@/pages/admin/Exploitation";
import AdminDispatch from "@/pages/admin/Dispatch";
import AdminCalendar from "@/pages/admin/Calendar";
import AdminLineDetail from "@/pages/admin/LineDetail";
import AdminDriverDetail from "@/pages/admin/DriverDetail";
import PublicRoute from "@/pages/public/PublicRoute";
import AdminDrivers from "@/pages/admin/Drivers";
import AdminUsers from "@/pages/admin/Users";
import AdminSettings from "@/pages/admin/Settings";
import AdminLogitrakSync from "@/pages/admin/LogitrakSync";
import AdminDriverPerformance from "@/pages/admin/DriverPerformance";
import AdminAIRecommendations from "@/pages/admin/AIRecommendations";
import AdminDashboardBus from "@/pages/admin/DashboardBus";
import AdminDashboardServices from "@/pages/admin/DashboardServices";
import AdminDashboardDrivers from "@/pages/admin/DashboardDrivers";

import ClientBusLive from "@/pages/client/ClientBusLive";

import DriverLayout from "@/layouts/DriverLayout";
import DriverMissions from "@/pages/driver/Missions";
import DriverMissionDetail from "@/pages/driver/MissionDetail";
import DriverScan from "@/pages/driver/Scan";

import ParentLayout from "@/layouts/ParentLayout";
import ParentHome from "@/pages/parent/Home";
import ParentChildren from "@/pages/parent/Children";
import ParentReservations from "@/pages/parent/Reservations";
import ParentTracking from "@/pages/parent/Tracking";
import ParentNotifications from "@/pages/parent/Notifications";

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  if (!user) return <Landing />;
  const target = user.role === "admin" ? "/admin" : user.role === "driver" ? "/driver" : "/parent";
  return <Navigate to={target} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/client/bus-live/:token" element={<ClientBusLive />} />
            <Route path="/public/route/:routeId" element={<PublicRoute />} />

            <Route path="/admin" element={
              <ProtectedRoute roles={["admin"]}><AdminLayout /></ProtectedRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="children" element={<AdminChildren />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="routes" element={<AdminRoutes />} />
              <Route path="missions" element={<AdminMissions />} />
              <Route path="incidents" element={<AdminIncidents />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="live-tracking" element={<AdminLiveTracking />} />
              <Route path="gtfs-trips" element={<AdminGtfsTrips />} />
              <Route path="gtfs-trips/:tripId" element={<AdminGtfsTripDetail />} />
              <Route path="fleet-mapping" element={<AdminFleetMapping />} />
              <Route path="stops-map" element={<AdminPoiMbc />} />
              <Route path="stops-map/supervision" element={<AdminStopsMap />} />
              <Route path="stops" element={<AdminStops />} />
              <Route path="inter-stops" element={<AdminInterStops />} />
              <Route path="bluetooth-tags" element={<AdminBluetoothTags />} />
              <Route path="affectations" element={<AdminAffectations />} />
              <Route path="affectations/bulk" element={<AdminAffectationsBulk />} />
              <Route path="programs" element={<AdminPrograms />} />
              <Route path="fleet-assignments" element={<AdminFleetAssignments />} />
              <Route path="substitutions" element={<AdminSubstitutions />} />
              <Route path="exploitation" element={<AdminExploitation />} />
              <Route path="dispatch" element={<AdminDispatch />} />
              <Route path="calendar" element={<AdminCalendar />} />
              <Route path="control-room" element={<Navigate to="/admin/live-tracking" replace />} />
              <Route path="lines/:routeId" element={<AdminLineDetail />} />
              <Route path="drivers/:driverId" element={<AdminDriverDetail />} />
              <Route path="drivers" element={<AdminDrivers />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="navixy-sync" element={<AdminLogitrakSync />} />
              <Route path="tournees" element={<Navigate to="/admin/gtfs-trips" replace />} />
              <Route path="driver-performance" element={<AdminDriverPerformance />} />
              <Route path="ai-recommendations" element={<AdminAIRecommendations />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="dashboards/bus" element={<AdminDashboardBus />} />
              <Route path="dashboards/services" element={<AdminDashboardServices />} />
              <Route path="dashboards/drivers" element={<AdminDashboardDrivers />} />
              <Route path="pricing" element={<AdminPricing />} />
            </Route>

            <Route path="/driver" element={
              <ProtectedRoute roles={["driver", "admin"]}><DriverLayout /></ProtectedRoute>
            }>
              <Route index element={<DriverMissions />} />
              <Route path="scan" element={<DriverScan />} />
              <Route path="mission/:id" element={<DriverMissionDetail />} />
            </Route>

            <Route path="/parent" element={
              <ProtectedRoute roles={["parent", "admin"]}><ParentLayout /></ProtectedRoute>
            }>
              <Route index element={<ParentHome />} />
              <Route path="children" element={<ParentChildren />} />
              <Route path="reservations" element={<ParentReservations />} />
              <Route path="tracking" element={<ParentTracking />} />
              <Route path="tracking/:missionId" element={<ParentTracking />} />
              <Route path="notifications" element={<ParentNotifications />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
